import { Capacitor, registerPlugin } from "@capacitor/core";

const DriverTracking = registerPlugin("DriverTracking");

let runtimeTrackingOrderId = "";
let runtimeTrackingAuthToken = "";

function normalizeAuthToken(value) {
  return String(value || "")
    .replace(/^Bearer\s+/i, "")
    .trim();
}

export function isCapacitorNativeRuntime() {
  try {
    const platform = String(Capacitor?.getPlatform?.() || "").toLowerCase();
    const isNative = Capacitor?.isNativePlatform?.() === true;
    return isNative && platform === "android";
  } catch {
    return false;
  }
}

export function canUseCapacitorNativeTracking() {
  try {
    if (!isCapacitorNativeRuntime()) return false;

    if (typeof Capacitor?.isPluginAvailable === "function") {
      return Capacitor.isPluginAvailable("DriverTracking");
    }

    return true;
  } catch {
    return false;
  }
}

// Compatibility export only.
// Legacy URI START/STOP transport is retired.
export function canUseNativeTrackingScheme() {
  return false;
}

export function canUseNativeTracking() {
  return canUseCapacitorNativeTracking();
}

export function shouldUseWebGpsPolling() {
  return !canUseCapacitorNativeTracking();
}

export async function startNativeTracking(orderId, driverToken, intervalSec = 30) {
  try {
    const id = String(orderId || "").trim();
    const authToken = normalizeAuthToken(driverToken);

    if (!id || !authToken) return false;
    if (!canUseCapacitorNativeTracking()) return false;

    const interval = Math.max(10, Math.min(300, Number(intervalSec) || 30));

    await DriverTracking.startTracking({
      orderId: id,
      authToken,
      intervalSec: interval,
    });

    runtimeTrackingOrderId = id;
    runtimeTrackingAuthToken = authToken;
    return true;
  } catch {
    return false;
  }
}

export async function stopNativeTracking(orderId) {
  try {
    const id = String(orderId || runtimeTrackingOrderId || "").trim();
    if (!id) return false;
    if (!canUseCapacitorNativeTracking()) return false;

    await DriverTracking.stopTracking({ orderId: id });

    runtimeTrackingOrderId = "";
    runtimeTrackingAuthToken = "";
    return true;
  } catch {
    return false;
  }
}

/**
 * Keep at most one native foreground GPS order.
 * Driver identity is resolved server-side from AuthSession.
 * The session token is opaque and is never parsed as a Driver ObjectId.
 */
export function syncNativeTrackingForOrders(orders, driverToken, intervalSec = 30) {
  try {
    const nativeEnabled = canUseNativeTracking();
    const authToken = normalizeAuthToken(driverToken);
    const storedLast = localStorage.getItem("nativeTracking.lastOrderId");

    if (!nativeEnabled || !authToken) {
      const toStop = runtimeTrackingOrderId || storedLast || "";

      runtimeTrackingOrderId = "";
      runtimeTrackingAuthToken = "";

      if (storedLast) {
        localStorage.removeItem("nativeTracking.lastOrderId");
      }

      if (toStop && nativeEnabled) {
        void stopNativeTracking(toStop);
      }
      return;
    }

    const list = Array.isArray(orders) ? orders : [];
    const trackStatuses = new Set(["ON_ROUTE", "AT_PICKUP", "LOADED"]);

    const active = list.find((o) =>
      trackStatuses.has(String(o?.status || "").toUpperCase())
    );

    const activeId = String(
      active?.id || active?._id || active?.orderId || ""
    ).trim();

    if (activeId) {
      const sessionChanged =
        runtimeTrackingOrderId !== activeId ||
        runtimeTrackingAuthToken !== authToken;

      if (sessionChanged) {
        void startNativeTracking(activeId, authToken, intervalSec).then((ok) => {
          if (ok) {
            localStorage.setItem("nativeTracking.lastOrderId", activeId);
          }
        });
      } else if (storedLast !== activeId) {
        localStorage.setItem("nativeTracking.lastOrderId", activeId);
      }

      return;
    }

    const toStop = runtimeTrackingOrderId || storedLast || "";

    runtimeTrackingOrderId = "";
    runtimeTrackingAuthToken = "";

    if (storedLast) {
      localStorage.removeItem("nativeTracking.lastOrderId");
    }

    if (toStop) {
      void stopNativeTracking(toStop);
    }
  } catch {}
}
